import {
  Arr,
  Bool,
  Num,
  Obj,
  OptNum,
  OptObj,
  OptStr,
  OptUnknown,
  Str,
  Unsafe,
} from '@shared/api/core/api_schema';
import {
  AcceptTermsSchema,
  AccountSchema,
  CodeRequestSchema,
  CodeResponseSchema,
  DoorCountersDataPointSchema,
  DoorSchema,
  DoorStateSchema,
  FrontendDoorMessageSchema,
  FullGroupSchema,
  LoginRequestSchema,
  FrontendMediaSchema,
  UpdateAccountSchema,
  MediaId,
  AccountNotificationStatus,
  FrontendAccountNotificationSchema,
  FrontendDoorMetadataSchema,
  DoorMessageCategory,
  DoorMessageLevel,
  CounterBreakdown,
  FrontendDoorMessage,
  DoorAccountNotificationMode,
  FrontendSupportMessageSchema,
  AnomalyMessageCategory,
  SupportMessageId,
  SupportGroupMemberSchema,
  DoorSchemaWithoutDoorCycles,
} from '@shared/models/api_models';
import {PreciseTimestamp} from '@shared/models/core_models';
import {DoorCadModelKey} from '@shared/models/door_cad_registry';
import {SupportMessageThreadId} from '@shared/models/mavismart_ids';

const TrackingEventSchema = Obj({
  email: Str(),
  ts: Num(),
  action: Str(),
  route: Str(),
  ip: Str(),
  userAgent: Str(),
  metadata: Unsafe<Record<string, unknown>>(),
  geo: OptObj({
    country: OptStr(),
    region: OptStr(),
    city: OptStr(),
    postalCode: OptStr(),
    latitude: OptNum(),
    longitude: OptNum(),
  }),
});

export const MAVISMART_BACKEND = {
  '/agent-chat': {
    POST: {
      req: Obj({
        messages: Arr(
          Obj({
            role: Str<'user' | 'assistant'>(),
            content: Str(),
          })
        ),
        modelTier: Str<'fast' | 'medium' | 'slow'>(),
        context: Obj({
          pageName: Str(),
          route: Str(),
          doorId: OptStr(),
          groupId: OptStr(),
          locale: Str(),
        }),
      }),
      res: Obj({
        response: Str(),
        sources: Arr(
          Obj({
            type: Str<'document' | 'api'>(),
            name: Str(),
            page: OptStr(),
          })
        ),
        metadata: Obj({
          correlationId: Str(),
          toolsUsed: Arr(
            Obj({
              name: Str(),
              durationMs: Num(),
            })
          ),
          tokens: Obj({
            input: Num(),
            output: Num(),
            total: Num(),
          }),
          estimatedCostUsd: Num(),
          modelId: Str(),
          durationMs: Num(),
        }),
      }),
    },
  },
  '/code': {
    POST: {
      req: CodeRequestSchema,
      res: CodeResponseSchema,
      public: true,
    },
  },
  '/login': {
    POST: {
      req: LoginRequestSchema,
      res: AccountSchema,
    },
  },
  '/logout': {
    POST: {
      req: Obj({}),
      res: Obj({success: Bool()}),
    },
  },
  '/accept-terms': {
    POST: {
      req: AcceptTermsSchema,
      res: AccountSchema,
    },
  },
  '/list-groups': {
    POST: {
      req: Obj({}),
      res: Obj({groups: Arr(FullGroupSchema)}),
    },
  },
  '/list-all-doors': {
    POST: {
      req: Obj({}),
      res: Obj({groups: Arr(FullGroupSchema), doors: Arr(DoorSchemaWithoutDoorCycles)}),
    },
  },
  '/get-door': {
    POST: {
      req: Obj({id: Str(), mockTecmotionGetStates: OptUnknown()}),
      res: DoorSchema,
    },
  },
  '/batch-get-door-metadata': {
    POST: {
      req: Obj({doorIds: Arr(Str())}),
      res: Obj({metadata: Arr(FrontendDoorMetadataSchema)}),
    },
  },
  '/list-door-messages': {
    POST: {
      req: Obj({
        id: Str(),
        category: OptStr<DoorMessageCategory>(),
        paginationToken: OptStr(),
        limit: Num(),
      }),
      res: Obj({
        messages: Arr(FrontendDoorMessageSchema),
        nextPaginationToken: OptStr(),
      }),
    },
  },
  '/door-messages-summary': {
    POST: {
      req: Obj({
        id: Str(),
        limitByCategory: OptNum(),
      }),
      res: Obj({
        messages: Unsafe<Record<string, FrontendDoorMessage[]>>(),
      }),
    },
  },
  '/list-account-notifications': {
    POST: {
      req: Obj({
        status: OptStr<AccountNotificationStatus>(),
        paginationToken: OptStr(),
        limit: Num(),
      }),
      res: Obj({
        notifications: Arr(FrontendAccountNotificationSchema),
        nextPaginationToken: OptStr(),
      }),
    },
  },
  '/get-account-notification': {
    POST: {
      req: Obj({
        ts: Num<PreciseTimestamp>(),
      }),
      res: Obj({
        notification: FrontendAccountNotificationSchema,
      }),
    },
  },
  '/update-account-notification-status': {
    POST: {
      req: Obj({ts: Num(), newStatus: Str<AccountNotificationStatus>()}),
      res: FrontendAccountNotificationSchema,
    },
  },
  '/read-all-account-notifications': {
    POST: {
      req: Obj({}),
      res: Obj({success: Bool()}),
    },
  },
  '/get-door-counters': {
    POST: {
      req: Obj({
        doorId: Str(),
        breakdown: Str<CounterBreakdown>(),
        counters: Arr(Str()),
        range: Obj({startTs: Num(), endTs: Num()}),
      }),
      res: Arr(DoorCountersDataPointSchema),
    },
  },
  '/support-get-door-state': {
    POST: {
      req: Obj({doorId: Str()}),
      res: DoorStateSchema,
    },
  },
  '/support-list-door-emails': {
    POST: {
      req: Obj({doorId: Str()}),
      res: Obj({groupMembers: Arr(SupportGroupMemberSchema)}),
    },
  },
  '/get-account': {
    POST: {
      req: Obj({}),
      res: AccountSchema,
    },
  },
  '/update-account': {
    POST: {
      req: UpdateAccountSchema,
      res: AccountSchema,
    },
  },
  '/delete-account': {
    POST: {
      req: Obj({}),
      res: Obj({success: Bool()}),
    },
  },
  '/update-door-label': {
    POST: {
      req: Obj({doorId: Str(), previousLabel: OptStr(), newLabel: Str()}),
      res: DoorSchema,
    },
  },
  '/update-door-custom-address': {
    POST: {
      req: Obj({doorId: Str(), previousCustomAddress: OptStr(), newCustomAddress: Str()}),
      res: DoorSchema,
    },
  },
  '/update-door-notification-mode': {
    POST: {
      req: Obj({doorId: Str(), notificationMode: Str<DoorAccountNotificationMode>()}),
      res: DoorSchema,
    },
  },
  '/add-collaborator': {
    POST: {
      req: Obj({groupId: Str(), email: Str()}),
      res: Obj({success: Bool()}),
    },
  },
  '/remove-collaborator': {
    POST: {
      req: Obj({groupId: Str(), email: Str()}),
      res: Obj({success: Bool()}),
    },
  },
  '/list-collaborators': {
    POST: {
      req: Obj({groupId: Str()}),
      res: Obj({collaborators: Arr(Str())}),
    },
  },
  '/create-custom-group': {
    POST: {
      req: Obj({name: OptStr()}),
      res: FullGroupSchema,
    },
  },
  '/add-door-to-custom-group': {
    POST: {
      req: Obj({groupId: Str(), doorId: Str()}),
      res: Obj({success: Bool()}),
    },
  },
  '/remove-door-from-custom-group': {
    POST: {
      req: Obj({groupId: Str(), doorId: Str()}),
      res: Obj({success: Bool()}),
    },
  },
  '/update-group-name': {
    POST: {
      req: Obj({groupId: Str(), newName: Str()}),
      res: Obj({success: Bool()}),
    },
  },
  '/delete-custom-group': {
    POST: {
      req: Obj({groupId: Str()}),
      res: Obj({success: Bool()}),
    },
  },
  '/create-media': {
    POST: {
      req: Obj({
        doorId: Str(),
        contentType: Str(),
        contentLength: Num(),
        fileName: Str(),
      }),
      res: Obj({
        mediaId: Str<MediaId>(),
        presignedPostUrl: Obj({
          url: Str(),
          fields: Unsafe<Record<string, string>>(),
        }),
      }),
    },
  },
  '/delete-media': {
    POST: {
      req: Obj({mediaId: Str<MediaId>()}),
      res: Obj({success: Bool()}),
    },
  },
  '/get-media': {
    POST: {
      req: Obj({mediaId: Str<MediaId>()}),
      res: Obj({media: FrontendMediaSchema}),
    },
  },
  '/get-door-3d-model': {
    POST: {
      req: Obj({doorId: Str()}),
      res: Obj({
        // `undefined` when the door's range has no CAD model: a normal, expected outcome.
        model: OptObj({
          modelKey: Str<DoorCadModelKey>(),
          label: Str(),
          // Short-lived when the geometry is confidential, hence never cached by the frontend.
          modelUrl: Str(),
          expiresInSeconds: OptNum(),
        }),
      }),
    },
  },
  '/resolve-door-anomaly-category': {
    POST: {
      req: Obj({
        doorId: Str(),
        anomalyCategory: Str<AnomalyMessageCategory>(),
        mockTecmotionGetState: OptStr(),
      }),
      res: Obj({
        newDoorStatus: Str<DoorMessageLevel>(),
        newDoorMessages: Arr(FrontendDoorMessageSchema),
      }),
    },
  },
  '/create-support-message': {
    POST: {
      req: Obj({
        doorId: Str(),
        doorMessageTs: OptNum<PreciseTimestamp>(),
        content: Str(),
        mediaIds: Arr(Str<MediaId>()),
      }),
      res: Obj({supportMessage: FrontendSupportMessageSchema}),
    },
  },
  '/support-get-message': {
    POST: {
      req: Obj({
        messageId: Str<SupportMessageId>(),
        threadId: OptStr<SupportMessageThreadId>(),
      }),
      res: Obj({supportMessage: FrontendSupportMessageSchema}),
    },
  },
  '/list-tracking-events': {
    POST: {
      req: Obj({
        email: OptStr(),
        action: OptStr(),
        startTs: OptNum(),
        endTs: OptNum(),
        paginationToken: OptStr(),
        limit: Num(),
      }),
      res: Obj({
        events: Arr(TrackingEventSchema),
        nextPaginationToken: OptStr(),
      }),
    },
  },
  '/get-tracking-stats': {
    POST: {
      req: Obj({
        startTs: Num(),
        endTs: Num(),
      }),
      res: Obj({
        totalEvents: Num(),
        uniqueUsers: Num(),
        avgEventsPerDay: Num(),
        topAction: Str(),
        eventsByAction: Unsafe<Record<string, number>>(),
        eventsByDay: Arr(Obj({day: Str(), count: Num()})),
        topRegions: Arr(Obj({name: Str(), count: Num()})),
        topCities: Arr(Obj({name: Str(), count: Num()})),
      }),
    },
  },
};
